// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ClearSelectedFolderAction,
  CreateFolderSuccessAction,
  DeleteFolderSuccessAction,
  GetFoldersSuccessAction,
  SetFoldersAction,
  SetProjectsInFolderAction,
  SetSelectedFolderAction,
  UpdateFolderSuccessAction,
  UpdateProjectThumbnailAction,
} from './folderListActions';
import { DeleteProjectsSuccessAction } from '../projectList/projectListActions';
import { UpdateProjectReferenceIdAction } from '../project/projectActions';

interface IFolderListState {
  foldersLoading: boolean;
  foldersCount: number;
  loadAll: boolean;
  loadNew: boolean;
  page: number;
  folderList: IFolder[];
  keywordFilter: string;
  selectedFolderId: string | null;
  selectedFolderName: string;
  error: string | null;
  projectsInFolder: IProject[];
  selectedFolder: IFolder | null;
}

const initialState: IFolderListState = {
  foldersLoading: false,
  foldersCount: 0,
  loadAll: false,
  loadNew: true,
  page: 0,
  folderList: [],
  keywordFilter: '',
  selectedFolderId: null,
  selectedFolderName: '',
  error: null,
  projectsInFolder: [],
  selectedFolder: null,
};

const folderListSlice = createSlice({
  name: 'folderList',
  initialState,
  reducers: {
    setKeywordFilter: (state, action: PayloadAction<string>) => {
      state.keywordFilter = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setFoldersCount: (state, action: PayloadAction<number>) => {
      state.foldersCount = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFolderList: (state) => {
      state.folderList = [];
      state.page = 0;
      state.loadAll = false;
      state.loadNew = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(CreateFolderSuccessAction, (state, action) => {
        state.folderList = [action.payload, ...state.folderList];
        state.foldersCount = state.folderList.length;
      })
      .addCase(GetFoldersSuccessAction, (state, action) => {
        state.foldersLoading = false;
        state.folderList = [...state.folderList, ...action.payload];
        state.foldersCount = state.folderList.length;
        state.error = null;
      })
      .addCase(SetFoldersAction, (state, action) => {
        state.folderList = action.payload;
        state.foldersCount = action.payload.length;
      })
      .addCase(DeleteFolderSuccessAction, (state, action) => {
        const updatedFolderList = state.folderList.filter((folder) => folder._id !== action.payload);
        state.folderList = updatedFolderList;
        state.foldersCount = updatedFolderList.length;
      })
      .addCase(UpdateFolderSuccessAction, (state, action) => {
        const updatedFolderList = state.folderList.map((folder) => (folder._id === action.payload._id ? { ...action.payload, projectCount: folder.projectCount } : folder));
        state.folderList = updatedFolderList;
      })
      .addCase(SetProjectsInFolderAction, (state, action) => {
        state.projectsInFolder = action.payload;
      })
      // The folder page renders from projectsInFolder, and a project is most often deleted from
      // that page. Without this the deleted card stays on screen until something refetches the
      // folder, so the delete looks as though it did not take.
      .addCase(DeleteProjectsSuccessAction, (state, action) => {
        state.projectsInFolder = state.projectsInFolder.filter((project) => project._id !== action.payload);
      })
      .addCase(UpdateProjectThumbnailAction, (state, action) => {
        const updatedProjectsInFolder = state.projectsInFolder.map((project) => (project._id === action.payload.projectId ? { ...project, thumbnail: action.payload.thumbnail } : project));
        state.projectsInFolder = updatedProjectsInFolder;
      })
      // A Reference ID edited on the folder page's own row or card (AUT-6449).
      .addCase(UpdateProjectReferenceIdAction, (state, action) => {
        const project = state.projectsInFolder.find((p) => p._id === action.payload._id);
        if (project) project.referenceId = action.payload.referenceId;
      })
      .addCase(SetSelectedFolderAction, (state, action) => {
        state.selectedFolder = action.payload;
      })
      .addCase(ClearSelectedFolderAction, (state) => {
        state.selectedFolder = null;
      });
  },
});

export const {
  setKeywordFilter,
  setPage,
  setFoldersCount,
  clearError,
  resetFolderList,
} = folderListSlice.actions;

const { reducer } = folderListSlice;

export default reducer;
